import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ScrollWipeContext } from "./scrollwipeContext";

const clamp01 = (n) => Math.max(0, Math.min(1, n));
let _id = 0;

function splitWordsPreserveWhitespace(str) {
  return str.match(/\n|[^\S\n]+|\S+/g) || [];
}

export default function ScrollWipe({
  as: Tag = "p",
  className = "",
  children,
  softness = 0.1,
  baseOpacity = 0.12,
}) {
  const ref = useRef(null);
  const group = useContext(ScrollWipeContext);
  const idRef = useRef(null);
  if (idRef.current == null) idRef.current = `sw_${_id++}`;
  const id = idRef.current;

  const text = typeof children === "string" ? children : "";
  const wordTokens = useMemo(() => splitWordsPreserveWhitespace(text), [text]);

  // Each element tracks its OWN position relative to the seam
  const [localT, setLocalT] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const seam = group?.seam ?? 0.5;

    const update = () => {
      const seamY = seam * (window.innerHeight || 1);
      const rect = el.getBoundingClientRect();
      const raw = (seamY - rect.top) / Math.max(1, rect.height);
      setLocalT(clamp01(raw));
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [group?.seam]);

  useEffect(() => {
    if (!group) return;
    group.register(id, Array.from(text).length);
    return () => group.unregister(id);
  }, [group, id, text]);

  const rendered = useMemo(() => {
    if (typeof children !== "string") return children;
    const chars = Array.from(text);
    const totalChars = Math.max(1, chars.length - 1);
    let charCursor = 0;

    return wordTokens.map((tok, wi) => {
      if (tok === "\n") { charCursor += 1; return <br key={`br-${wi}`} />; }
      if (/^[^\S\n]+$/.test(tok)) {
        charCursor += tok.length;
        return <span key={`sp-${wi}`} style={{ whiteSpace: "pre-wrap" }}>{tok}</span>;
      }
      const tokChars = Array.from(tok);
      return (
        <span key={`w-${wi}`} style={{ display: "inline-block", whiteSpace: "nowrap" }}>
          {tokChars.map((ch, ci) => {
            const u = charCursor / totalChars;
            const local = clamp01((localT - u) / Math.max(1e-6, softness) + 1);
            const opacity = baseOpacity + (1 - baseOpacity) * local;
            charCursor += 1;
            return (
              <span
                key={`c-${wi}-${ci}`}
                style={{ opacity, display: "inline" }}
                aria-hidden="true"
              >
                {ch}
              </span>
            );
          })}
        </span>
      );
    });
  }, [children, wordTokens, text, localT, softness, baseOpacity]);

  return (
    <Tag ref={ref} className={className} aria-label={text}>
      {rendered}
    </Tag>
  );
}
